import { ApiError, type PartnerUser } from '@central/api';
import { Card, PageHeader, StatusChip } from '@central/ui';
import {
  Alert,
  Button,
  Checkbox,
  Group,
  Modal,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { keys, useApi } from '../../api';
import { DataTable, Empty, QueryState } from '../../components/parts';
import { errorMessage } from '../../lib/errors';
import { muted } from '../../lib/labels';
import { useMe } from '../../lib/me';
import { OneTimeLink, type IssuedLink } from '../Staff';

/**
 * The reseller's own team: every reseller user sees who is on it; its
 * reseller admins invite users, edit them, and re-send setup links.
 */
export function PartnerTeam() {
  const api = useApi();
  const me = useMe();
  const admin = me.user.resellerAdmin;
  const list = useQuery({ queryKey: keys.partnerTeam, queryFn: () => api.partner.team.list() });
  const [inviting, setInviting] = useState(false);
  const [editing, setEditing] = useState<string>();
  const items = list.data?.items ?? [];
  const target = items.find((u) => u.id === editing);

  return (
    <>
      <PageHeader
        title="Team"
        subtitle={
          admin
            ? 'Invite your engineers and manage who can sign in for your company.'
            : 'Your company’s console users. Ask an admin to invite or change someone.'
        }
        actions={
          admin && (
            <Button color="dark" size="sm" h={40} onClick={() => setInviting(true)}>
              Invite user
            </Button>
          )
        }
      />
      <Card title={`Team · ${items.length}`} padded={false}>
        <QueryState isPending={list.isPending} error={list.error}>
          {() =>
            items.length === 0 ? (
              <Empty>No users yet.</Empty>
            ) : (
              <DataTable
                label="Team"
                minWidth={640}
                head={['Name', 'Role', 'Status', ...(admin ? [''] : [])]}
              >
                {items.map((u) => (
                  <Table.Tr key={u.id}>
                    <Table.Td>
                      <div style={{ fontWeight: 600 }}>
                        {u.name}
                        {u.id === me.user.id && <span style={muted}> (you)</span>}
                      </div>
                      <div style={{ fontSize: 13, ...muted }}>{u.email}</div>
                    </Table.Td>
                    <Table.Td>
                      {u.resellerAdmin ? (
                        <StatusChip tone="vio">Admin</StatusChip>
                      ) : (
                        <StatusChip tone="blu">Engineer</StatusChip>
                      )}
                    </Table.Td>
                    <Table.Td>
                      {!u.enabled ? (
                        <StatusChip tone="unk">Disabled</StatusChip>
                      ) : u.setupPending ? (
                        <StatusChip tone="warn">Pending setup</StatusChip>
                      ) : (
                        <StatusChip tone="ok">Enabled</StatusChip>
                      )}
                    </Table.Td>
                    {admin && (
                      <Table.Td style={{ textAlign: 'right' }}>
                        <Button
                          variant="subtle"
                          size="compact-sm"
                          aria-label={`Edit ${u.name}`}
                          onClick={() => setEditing(u.id)}
                        >
                          Edit
                        </Button>
                      </Table.Td>
                    )}
                  </Table.Tr>
                ))}
              </DataTable>
            )
          }
        </QueryState>
      </Card>
      {admin && <Invite opened={inviting} onClose={() => setInviting(false)} />}
      {admin && target && (
        <Edit
          key={target.id}
          user={target}
          self={target.id === me.user.id}
          onClose={() => setEditing(undefined)}
        />
      )}
    </>
  );
}

function Invite({ opened, onClose }: { opened: boolean; onClose: () => void }) {
  const api = useApi();
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [resellerAdmin, setResellerAdmin] = useState(false);
  const [link, setLink] = useState<IssuedLink>();
  const create = useMutation({
    mutationFn: () =>
      api.partner.team.create({
        email: email.trim(),
        name: name.trim(),
        resellerAdmin: resellerAdmin || undefined,
      }),
    onSuccess: async (r) => {
      setLink({ url: r.setupUrl, what: 'setup link', email: r.user.email, emailed: r.emailed });
      await qc.invalidateQueries({ queryKey: keys.partnerTeam });
    },
  });
  const fields = create.error instanceof ApiError ? create.error.fields : undefined;

  const close = () => {
    setEmail('');
    setName('');
    setResellerAdmin(false);
    setLink(undefined);
    create.reset();
    onClose();
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate();
  };

  return (
    <Modal opened={opened} onClose={close} title="Invite user">
      {link ? (
        <Stack gap={14}>
          <OneTimeLink {...link} />
          <Group justify="flex-end">
            <Button color="dark" onClick={close}>
              Done
            </Button>
          </Group>
        </Stack>
      ) : (
        <form onSubmit={submit} noValidate>
          <Stack gap={14}>
            <TextInput
              label="Work email"
              type="email"
              data-autofocus
              value={email}
              error={fields?.email && 'Enter a valid email address.'}
              onChange={(e) => setEmail(e.currentTarget.value)}
            />
            <TextInput
              label="Name"
              value={name}
              error={fields?.name && 'Enter their name.'}
              onChange={(e) => setName(e.currentTarget.value)}
            />
            <Checkbox
              label="Admin"
              description="Can invite and manage your team."
              checked={resellerAdmin}
              onChange={(e) => setResellerAdmin(e.currentTarget.checked)}
            />
            {create.error && !fields && (
              <Alert color="red">
                {errorMessage(create.error, {
                  duplicate: 'A console user with that email already exists.',
                })}
              </Alert>
            )}
            <Group justify="flex-end">
              <Button variant="default" onClick={close}>
                Cancel
              </Button>
              <Button type="submit" color="dark" loading={create.isPending}>
                Invite and get link
              </Button>
            </Group>
          </Stack>
        </form>
      )}
    </Modal>
  );
}

function Edit({ user, self, onClose }: { user: PartnerUser; self: boolean; onClose: () => void }) {
  const api = useApi();
  const qc = useQueryClient();
  const [name, setName] = useState(user.name);
  const [link, setLink] = useState<IssuedLink>();
  const update = useMutation({
    mutationFn: (b: { name?: string; enabled?: boolean; resellerAdmin?: boolean }) =>
      api.partner.team.update(user.id, b),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.partnerTeam }),
  });
  const resend = useMutation({
    mutationFn: () => api.partner.team.setupLink(user.id),
    onSuccess: (r) =>
      setLink({ url: r.setupUrl, what: 'setup link', email: user.email, emailed: r.emailed }),
  });
  const error = update.error ?? resend.error;
  const fields = update.error instanceof ApiError ? update.error.fields : undefined;
  const busy = update.isPending || resend.isPending;

  return (
    <Modal opened onClose={onClose} title={`${user.name}${self ? ' (you)' : ''}`}>
      {link ? (
        <Stack gap={14}>
          <OneTimeLink {...link} />
          <Group justify="flex-end">
            <Button color="dark" onClick={onClose}>
              Done
            </Button>
          </Group>
        </Stack>
      ) : (
        <Stack gap={16}>
          <Text size="sm" style={muted}>
            {user.email}
          </Text>
          <Group align="flex-end" gap={8}>
            <TextInput
              label="Name"
              value={name}
              error={fields?.name && 'Enter their name.'}
              onChange={(e) => setName(e.currentTarget.value)}
              style={{ flex: 1 }}
            />
            <Button
              color="dark"
              disabled={name.trim() === user.name || busy}
              loading={update.isPending && update.variables?.name !== undefined}
              onClick={() => update.mutate({ name: name.trim() })}
            >
              Save name
            </Button>
          </Group>
          <Checkbox
            label="Admin"
            description={
              self ? 'You cannot remove your own admin rights.' : 'Can invite and manage your team.'
            }
            checked={user.resellerAdmin}
            disabled={self || busy}
            onChange={(e) => update.mutate({ resellerAdmin: e.currentTarget.checked })}
          />
          <Stack gap={6}>
            <Button
              variant="default"
              size="sm"
              style={{ alignSelf: 'flex-start' }}
              disabled={!user.enabled || busy}
              loading={resend.isPending}
              onClick={() => resend.mutate()}
            >
              {user.setupPending ? 'Resend setup link' : 'Password reset link'}
            </Button>
            <Text size="xs" style={muted}>
              The new link replaces any unused one and expires in 24 hours.
            </Text>
          </Stack>
          {error && !fields ? <Alert color="red">{errorMessage(error)}</Alert> : null}
          <Group justify="space-between">
            {self ? (
              <span />
            ) : (
              <Button
                variant="subtle"
                color={user.enabled ? 'red' : 'dark'}
                disabled={busy}
                loading={update.isPending && update.variables?.enabled !== undefined}
                onClick={() => update.mutate({ enabled: !user.enabled })}
              >
                {user.enabled ? 'Disable' : 'Enable'}
              </Button>
            )}
            <Button variant="default" onClick={onClose}>
              Close
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}
